import type { MouseEvent, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@jaks/core/api-client";
import { Icon } from "./icons.tsx";
import { Bell } from "./bell.tsx";
import { isApplePlatform, showsOn, tryApp } from "./open-link.ts";
import type { OpenEnv, OpenableLink } from "./open-link.ts";

/**
 * The tool row from the old portal, on both surfaces: icons with tooltips in the
 * desktop sidebar, labelled tiles under Tools on the phone's More page. The list
 * comes from `/api/home/quick-links`; what a click does is decided in
 * `open-link.ts`, which is where the iPhone and iPad behaviour is tested.
 */
export interface QuickLink extends OpenableLink {
  label: string;
}

const ICONS: Record<string, string> = {
  JobTread: "briefcase", Timeclock: "clock", Gmail: "mail", Calendar: "calendarDay", Drive: "driveTri", Capture: "viewfinder",
};

/** The real browser, read once. `visibilitychange` lives on the document; the other two on the window. */
const ENV: OpenEnv = {
  apple: typeof navigator !== "undefined" && isApplePlatform(navigator),
  navigate: (u) => { location.assign(u); },
  hidden: () => document.visibilityState === "hidden",
  onLeave: (cb) => {
    document.addEventListener("visibilitychange", cb);
    window.addEventListener("pagehide", cb);
    window.addEventListener("blur", cb);
    return () => {
      document.removeEventListener("visibilitychange", cb);
      window.removeEventListener("pagehide", cb);
      window.removeEventListener("blur", cb);
    };
  },
  now: () => Date.now(),
  after: (ms, cb) => { window.setTimeout(cb, ms); },
};

export function useQuickLinks(): QuickLink[] {
  const q = useQuery({
    queryKey: ["quick-links"],
    queryFn: () => api<{ links: QuickLink[] }>("/api/home/quick-links"),
    staleTime: 10 * 60_000,
  });
  return (q.data?.links ?? []).filter((l) => showsOn(l, ENV.apple));
}

/** One link on any surface: the app on an iPhone or iPad, the page in a new tab elsewhere. */
export function QuickLinkA({ link, className, children }: { link: QuickLink; className?: string; children: ReactNode }) {
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (tryApp(link, ENV)) e.preventDefault();
  };
  return (
    <a
      href={link.url ?? link.ios}
      target={link.url ? "_blank" : undefined}
      rel="noreferrer"
      className={className}
      title={link.label}
      aria-label={link.label}
      onClick={onClick}
    >
      {children}
    </a>
  );
}

export function linkIcon(label: string): string {
  return ICONS[label] ?? "open";
}

/**
 * The sidebar's row, as the old portal had it: one line, the links first, the
 * bell and the theme button at the end, every cell an equal share of the width.
 * James, 2026-09-05: "They were all on 1 line before with a gap for a page we
 * removed. I would like them all on one line again." The gap went with the page.
 */
export function QuickLinks({ onTheme }: { onTheme: () => void }) {
  const links = useQuickLinks();
  return (
    <div className="quick-links" aria-label="Quick links">
      {links.map((l) => (
        <QuickLinkA key={l.label} link={l}><Icon name={linkIcon(l.label)} size={16} /></QuickLinkA>
      ))}
      <Bell variant="sidebar" />
      <button title="Theme" onClick={onTheme} aria-label="Toggle theme">◐</button>
    </div>
  );
}

/** The phone's Tools section on More: one line of tiles that scrolls sideways when the screen is narrower than the six. */
export function QuickLinkTiles() {
  const links = useQuickLinks();
  if (!links.length) return null;
  return (
    <section aria-labelledby="more-g-tools" style={{ display: "grid", gap: 8 }}>
      <h3 id="more-g-tools" className="more-g">Tools</h3>
      <div className="more-tools">
        {links.map((l) => (
          <QuickLinkA key={l.label} link={l} className="more-tool">
            <Icon name={linkIcon(l.label)} size={22} />
            <span>{l.label}</span>
          </QuickLinkA>
        ))}
      </div>
    </section>
  );
}
